import { FileFolderNameExtensions } from "./FileFolderNameExtensions";
import { removeExtension } from "../components/helpers/stringHelper";
import { IFileToUpload } from "../interfaces/IFileToUpload";
import { IOfficeService } from "../interfaces/IOfficeService";
import { OfficeServiceEnum } from "../components/enums/OfficeService";
import { logger } from "../../../utilities/logger";
import { OutlookUtils } from "../../../utilities/OutlookUtils";
import { BatchRequestContent, BatchRequestStep, BatchResponseContent } from "@microsoft/microsoft-graph-client/lib/src/browser";
import { getAuthenticatedClient } from "./GraphClientService";
import authModule from "../../../utilities/AuthModule";

interface SelectedMailIem {
  itemId: string;
  normalizedSubject: string;
  attachments?: Office.AttachmentDetails[];
}

export class OutlookService implements IOfficeService {
  serviceType: OfficeServiceEnum;
  constructor() {
    this.serviceType = OfficeServiceEnum.outlook;
  }

  static getItemRestId = (itemId: string) => {
    if (itemId) {
      try {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
          return itemId;
        }
        else {
          return Office.context?.mailbox?.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
        }
      } catch (error) {
        logger(error, "OutlookService.ts (getItemRestId)");
      }
    }
    return null;
  };

  static getItemEWSId = (itemId: string) => {
    if (itemId) {
      try {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
          return null;
        }
        else {
          return itemId;
        }
      } catch (error) {
        logger(error, "OutlookService.ts (getItemEWSId)");
      }
    }
    return null;
  };

  getRootItemName = async () => {
    const { normalizedSubject } = Office.context?.mailbox?.item;
    return normalizedSubject;
  };

  getItems = async (autoSelectAttachments?: boolean): Promise<IFileToUpload[]> => {
    const filesToUpload: IFileToUpload[] = [];
    const selectedMailItems: SelectedMailIem[] = [];

    if (Office.context?.mailbox?.item) {
      const { itemId, normalizedSubject, attachments } = Office.context?.mailbox?.item;
      if (!itemId) return [];
      selectedMailItems.push({ itemId, normalizedSubject, attachments });
    }
    else if (Office.context?.mailbox?.getSelectedItemsAsync) {
      await new Promise(async (resolve) => {
        Office.context.mailbox.getSelectedItemsAsync(async asyncResult => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            logger(new Error(asyncResult.error.message), "OutlookService.ts (getSelectedItemsAsync)");
            resolve(false);
          }

          if (Array.isArray(asyncResult.value) && asyncResult.value.length > 0) {
            const mailboxResource: string = Office.context?.mailbox?.item?.getSharedPropertiesAsync
              ? `users/${await OutlookUtils.getMailboxAddress()}`
              : "me";

            const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

            for (let i = 0; i < asyncResult.value.length; i += 20) {
              const selectedItemSlices: Office.SelectedItemDetails[] = asyncResult.value.slice(i, i + 20);

              const batchRequests: BatchRequestStep[] = selectedItemSlices
                .map(selectedItem => {
                  return {
                    id: selectedItem.itemId,
                    request: new Request(`/${mailboxResource}/messages/${OutlookService.getItemRestId(selectedItem.itemId)}/attachments?$select=id,name,isInline,size,contentType`, {
                      method: "GET"
                    })
                  };
                });

              const content = await new BatchRequestContent(batchRequests).getContent();
              const batchResponse = await client.api("/$batch").post(content);
              const batchResponseContent = new BatchResponseContent(batchResponse);

              await Promise.all(selectedItemSlices.map(async selectedItem => {
                let attachments: Office.AttachmentDetails[];

                const attachmentsResponse = batchResponseContent.getResponseById(selectedItem.itemId);
                if (attachmentsResponse.ok) {
                  const selectedItemAttachments = (await attachmentsResponse.json())?.value;
                  if (Array.isArray(selectedItemAttachments) && selectedItemAttachments.length > 0) {
                    attachments = selectedItemAttachments
                      .filter(attachment => attachment?.id && !attachment.isInline && attachment.size > 0);
                  }
                }

                selectedMailItems.push({ itemId: selectedItem.itemId, normalizedSubject: selectedItem.subject, attachments });
              }));
            }
          }

          resolve(true);
        });
      });
    }

    for (let index = 0; index < selectedMailItems.length; index++) {
      try {
        const selectedMailItem = selectedMailItems[index];
        const { itemId, normalizedSubject, attachments } = selectedMailItem;

        const messageRestId: string = OutlookService.getItemRestId(itemId);

        const itemAttachments = Array.isArray(attachments) && attachments.length > 0
          ? attachments
            .filter(attachment => attachment?.id && !attachment.isInline && attachment.size > 0)
            .map(attachment => {
              return {
                id: OutlookService.getItemRestId(attachment.id),
                ewsId: OutlookService.getItemEWSId(attachment.id),
                parentId: messageRestId,
                name: removeExtension(FileFolderNameExtensions.EnsureValidName(attachment.name)),
                extension: OutlookUtils.getExtension(attachment),
                isSelected: autoSelectAttachments === true,
                isAttachment: true,
                isDuplicate: false,
                contentType: attachment.contentType
              };
            })
            .filter(attachment => attachment?.id)
          : [];

        const attachmentsWithEmptyExtension = itemAttachments.filter(a => a.ewsId && !a.extension && a.contentType && a.contentType !== "application/octet-stream");
        if (attachmentsWithEmptyExtension?.length > 0) {
          const ewsUrl: string = OutlookUtils.getEWSURL();
          const ewsToken: string = await OutlookUtils.getEWSToken();

          if (ewsUrl && ewsToken) {
            const extensionData = await OutlookUtils.requestExtensions(ewsUrl, ewsToken, attachmentsWithEmptyExtension.map(a => a.ewsId));
            if (Array.isArray(extensionData)) {
              for (let i = 0; i < extensionData.length; i++) {
                const extData = extensionData[i];
                const attachment = itemAttachments.find(a => a.ewsId === extData.id);
                if (attachment) {
                  attachment.extension = extData.extension;
                }
              }
            }
          }
        }

        const mainMailItem = {
          id: messageRestId,
          ewsId: OutlookService.getItemEWSId(itemId),
          name: FileFolderNameExtensions.EnsureValidName(normalizedSubject),
          extension: "eml",
          isSelected: autoSelectAttachments !== true || (autoSelectAttachments === true && !itemAttachments?.length),
          isAttachment: false,
          isDuplicate: false,
          isMainMailItem: true
        };

        filesToUpload.push(...[mainMailItem, ...itemAttachments]);
      } catch (error) {
        console.error(error);
      }
    }

    return filesToUpload;
  };
}
